import { useEffect, useState } from "react";
import { LoadingStatsGrid } from "@xpense/ui";
import { api, type MonthlyNutritionSummary, type NutritionTrend } from "../api/client";
import { EmptyState, MetricCard, Page, RetryButton, Section } from "../components/Page";
import NutritionHealthScore from "../components/NutritionHealthScore";
import NutritionMacroBar from "../components/NutritionMacroBar";
import NutritionTipsList from "../components/NutritionTipsList";
import NutritionTrendChart from "../components/NutritionTrendChart";
import ItemizationBanner from "../components/ItemizationBanner";

export default function Nutrition() {
  const [summary, setSummary] = useState<MonthlyNutritionSummary | null>(null);
  const [trend, setTrend] = useState<NutritionTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const month = new Date().toISOString().slice(0, 7);
      const [summaryData, trendData] = await Promise.all([
        api.get<MonthlyNutritionSummary>(`/api/v1/nutrition/monthly?month=${month}`),
        api.get<NutritionTrend>("/api/v1/nutrition/trends?months=6"),
      ]);
      setSummary(summaryData);
      setTrend(trendData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load nutrition data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Page eyebrow="Health & Nutrition" title="Nutrition Intelligence">
        <LoadingStatsGrid />
      </Page>
    );
  }

  if (error) {
    return (
      <Page eyebrow="Health & Nutrition" title="Nutrition Intelligence">
        <Section>
          <EmptyState icon="warning" title="Unable to load nutrition data" description={error} action={<RetryButton onClick={loadData} />} tone="danger" />
        </Section>
      </Page>
    );
  }

  if (!summary || summary.total_items === 0) {
    return (
      <Page
        eyebrow="Health & Nutrition"
        title="Nutrition Intelligence"
        description="Nutrition insights appear when food-related transactions are classified and enriched."
      >
        <ItemizationBanner />
        <Section>
          <EmptyState
            icon="activity"
            title="No nutrition data yet"
            description="Food transactions from restaurants, grocery stores, and delivery services will be analyzed here. Pending itemizations are shown above."
          />
        </Section>
      </Page>
    );
  }

  const monthLabel = summary.month
    ? new Date(`${summary.month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : "";

  const directionColor =
    summary.insight?.direction === "improving" ? "var(--xpn-color-success)"
    : summary.insight?.direction === "declining" ? "var(--xpn-color-danger)"
    : "var(--xpn-text-muted)";

  return (
    <Page
      eyebrow={monthLabel}
      title="Nutrition Intelligence"
      description="AI-powered analysis of your eating habits from spending data."
    >
      <ItemizationBanner />

      {/* Health Score + Summary */}
      <div className="xpn-grid xpn-grid--2" style={{ marginBottom: 24 }}>
        <div className="xpn-section" style={{ display: "flex", alignItems: "center", gap: 24, padding: 24 }}>
          <NutritionHealthScore score={summary.health_score} size="lg" />
          <div>
            <div className="xpn-text-heading-sm xpn-text" style={{ marginBottom: 4 }}>
              Nutrition Score
            </div>
            <div className="xpn-text-body-md xpn-text-muted" style={{ marginBottom: 8 }}>
              Based on {summary.total_items} items from {summary.total_food_transactions} transactions
            </div>
            {summary.insight && (
              <div className="xpn-text-body-md xpn-text" style={{ color: directionColor, fontWeight: 600 }}>
                Trend: {summary.insight.direction}
                {summary.insight.direction === "improving" ? " ↑" : summary.insight.direction === "declining" ? " ↓" : " →"}
              </div>
            )}
          </div>
        </div>

        <div className="xpn-section" style={{ padding: 24 }}>
          <div className="xpn-text-heading-sm xpn-text" style={{ marginBottom: 12 }}>Monthly Summary</div>
          <div className="xpn-grid xpn-grid--2" style={{ gap: 12 }}>
            <MetricCard label="Healthy items" value={summary.healthy_count} caption={`${summary.total_items > 0 ? ((summary.healthy_count / summary.total_items) * 100).toFixed(0) : 0}% of total`} tone="success" />
            <MetricCard label="Unhealthy items" value={summary.unhealthy_count} caption={`${summary.total_items > 0 ? ((summary.unhealthy_count / summary.total_items) * 100).toFixed(0) : 0}% of total`} tone={summary.unhealthy_count > summary.healthy_count ? "danger" : "neutral"} />
            <MetricCard label="Total calories" value={summary.total_calories.toLocaleString()} caption="Estimated monthly intake" tone="info" />
            <MetricCard label="Items needing input" value={summary.healthy_count + summary.unhealthy_count === 0 ? summary.total_items : 0} caption="Pending itemization" tone="warning" />
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(summary.alcohol_count > 0 || summary.tobacco_count > 0) && (
        <div className="xpn-card" style={{ border: "1px solid var(--xpn-color-warning)", padding: 16, marginBottom: 24 }}>
          <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650, marginBottom: 8 }}>
            ⚠️ Health Alert
          </div>
          {summary.alcohol_count > 0 && (
            <div className="xpn-text-body-md xpn-text-muted" style={{ marginBottom: 4 }}>
              Alcohol detected {summary.alcohol_count} time{summary.alcohol_count > 1 ? "s" : ""} this month.
            </div>
          )}
          {summary.tobacco_count > 0 && (
            <div className="xpn-text-body-md xpn-text-muted">
              Tobacco products detected. Consider speaking with a healthcare professional.
            </div>
          )}
        </div>
      )}

      {/* Health alert for low score */}
      {summary.health_score < 40 && (
        <div className="xpn-card" style={{ border: "1px solid var(--xpn-color-danger)", padding: 16, marginBottom: 24 }}>
          <div className="xpn-text-body-md xpn-text" style={{ fontWeight: 650, marginBottom: 4, color: "var(--xpn-color-danger)" }}>
            🚨 Low Nutrition Score
          </div>
          <div className="xpn-text-body-md xpn-text-muted">
            Your nutrition score is below 40. Check the tips below for personalized course-correction suggestions.
          </div>
        </div>
      )}

      {/* Macro Breakdown */}
      <Section title="Macro Breakdown" description="Average macros per food item this month.">
        <NutritionMacroBar macros={summary.macros_avg} />
        <div className="xpn-grid xpn-grid--4" style={{ marginTop: 12 }}>
          <MetricCard label="Fiber" value={`${summary.macros_avg.fiber_g.toFixed(1)}g`} caption="Avg per item" tone="info" />
          <MetricCard label="Sugar" value={`${summary.macros_avg.sugar_g.toFixed(1)}g`} caption="Avg per item" tone={summary.macros_avg.sugar_g > 10 ? "danger" : "neutral"} />
        </div>
      </Section>

      {/* Category Breakdown */}
      <Section title="Health Category Breakdown" description="Distribution of food items by health category.">
        <div className="xpn-section-body">
          {summary.health_category_breakdown.map((cat) => (
            <div className="xpn-category-row" key={cat.category}>
              <div>
                <span className="xpn-text-body-md xpn-text" style={{ fontWeight: 650, textTransform: "capitalize" }}>
                  {cat.category.replace(/_/g, " ")}
                </span>
                <div className="xpn-text-caption xpn-text-muted">{cat.count} items</div>
              </div>
              <div className="xpn-flex-column" style={{ alignItems: "flex-end" }}>
                <div data-tnum className="xpn-text-body-tabular xpn-text">
                  {cat.percentage.toFixed(0)}%
                </div>
                <div className="xpn-progress" style={{ width: 80 }}>
                  <div className="xpn-progress-bar" style={{ width: `${Math.max(cat.percentage, 2)}%`, background: "var(--xpn-color-primary)" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Top Food Items */}
      <Section title="Top Food Items" description="Most frequently consumed items this month.">
        <div className="xpn-section-body">
          {summary.top_food_items.map((item) => (
            <div className="xpn-category-row" key={item.item_name}>
              <span className="xpn-text-body-md xpn-text" style={{ fontWeight: 650 }}>
                {item.item_name}
              </span>
              <div className="xpn-text-body-tabular xpn-text-muted">
                {item.count} time{item.count > 1 ? "s" : ""}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Trend Chart */}
      {trend && trend.months.length > 1 && (
        <Section title="Health Score Trend" description="How your nutrition score has changed over time.">
          <NutritionTrendChart months={trend.months} />
          <div className="xpn-text-caption xpn-text-muted" style={{ marginTop: 8, textAlign: "center" }}>
            Direction: <span style={{ fontWeight: 600, color: directionColor }}>{trend.direction}</span>
          </div>
        </Section>
      )}

      {/* AI Insights */}
      {summary.insight && (
        <Section title="AI Health Insights" description="Personalized analysis and recommendations.">
          <NutritionTipsList
            summary={summary.insight.summary}
            strengths={summary.insight.strengths}
            concerns={summary.insight.concerns}
            tips={summary.insight.tips}
          />
        </Section>
      )}
    </Page>
  );
}
